import React, {
  createContext,
  CSSProperties, ReactNode,
  useContext,
} from 'react';
import { Col, Row } from 'antd';
import { DivisionProviderProps, DivisionSelectorProvider } from './provider';
import {
  BlockSelectorFunc,
  BlockSelectorProp,
  DivisionSelectorFunc,
  DivisionSelectorProp,
  FloorSelectorFunc,
  FloorSelectorProp,
} from './division_selector';
import styles from './index.module.scss';

/**
 * 街区・フロア・区画のセレクターのプロパティ
 */
export type ComponentProps = Omit<DivisionProviderProps, 'children'> & { baseClassName?: string, selectStyle?: CSSProperties, children?: ReactNode };

/**
 * 街区・フロア・区画のセレクターを作成するHookの返却値
 */
export type UseDivisionValue = {
  /** 街区・フロア・区画全てを包括したコンポーネント */
  DivisionSelector: React.FC<ComponentProps>;
  /** 街区・フロア・区画の各コンポーネントのパーツ */
  Parts: {
    /** 共通変数を格納するプロバイダー。 Division, Floor, Block はこの Provider で囲んだ中で利用する必要がある */
    DivisionsProvider: React.FC<DivisionProviderProps>;
    /** 街区選択ボックス */
    Division : React.FC<DivisionSelectorProp>;
    /** フロア選択ボックス */
    Floor: React.FC<FloorSelectorProp>;
    /** 区画選択ボックス */
    Block: React.FC<BlockSelectorProp>;
  }
};

/**
 * 選択ボックスのオプション
 */
export type Option = {
  value: number;
  label: string;
};

/**
 * 街区・フロア・区画のセレクターを作成するHookの共有変数（internal)
 */
export type IDivisionSelectorContext = {
  divisionId: number;
  floorId: number;
  blockId: number;
  update: (args: { divisionId?: number, floorId?: number, blockId?: number }) => void;
};

/**
 * 街区・フロア・区画のセレクターを作成するHook
 */
export const useDivisionSelector = (): UseDivisionValue => {
  const DivisionSelectorContext = createContext<IDivisionSelectorContext>({ divisionId: 0, floorId: 0, blockId: 0, update: () => {} });
  const useDivisionContext = () => useContext<IDivisionSelectorContext>(DivisionSelectorContext);

  return {
    Parts: {
      DivisionsProvider: DivisionSelectorProvider(DivisionSelectorContext),
      Division: DivisionSelectorFunc(useDivisionContext),
      Floor: FloorSelectorFunc(useDivisionContext),
      Block: BlockSelectorFunc(useDivisionContext),
    },
    DivisionSelector: ({ children, baseClassName = styles.row, selectStyle = { width: 120 }, ...props }) => {
      const Provider = DivisionSelectorProvider(DivisionSelectorContext);
      const Division = DivisionSelectorFunc(useDivisionContext);
      const Floor = FloorSelectorFunc(useDivisionContext);
      const Block = BlockSelectorFunc(useDivisionContext);

      return (
          <Row className={baseClassName}>
            <Provider {...props}>
                <div className={styles.formLabel}>
                  街区
                </div>
                <Col span={4}>
                  <Division selectStyle={selectStyle}/>
                </Col>
                <div className={styles.formLabel2}>
                  階
                </div>
                <Col span={4}>
                  <Floor selectStyle={selectStyle}/>
                </Col>
                <div className={styles.formLabel2}>
                  区画
                </div>
                <Col>
                  <Block selectStyle={selectStyle}/>
                </Col>
                  {children}
            </Provider>
          </Row>
      );
    },
  };
};
